import React, { useState } from 'react';
import { X, Building2, Key, Users } from 'lucide-react';
import { OrganizationSettings } from './OrganizationSettings';
import { IdPConfigForm } from './IdPConfigForm';
import { SCIMSettings } from './SCIMSettings';

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type SettingsTab = 'organization' | 'idp' | 'scim';

export function SettingsDialog({ open, onOpenChange }: SettingsDialogProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('organization');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  if (!open) return null;

  const tabs: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
    { id: 'organization', label: 'Organization', icon: <Building2 className="w-4 h-4" /> },
    { id: 'idp', label: 'Identity Provider', icon: <Key className="w-4 h-4" /> },
    { id: 'scim', label: 'SCIM Provisioning', icon: <Users className="w-4 h-4" /> },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => onOpenChange(false)}
      />

      {/* Dialog */}
      <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold">Settings</h2>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1 rounded-md hover:bg-accent transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          <div className="w-56 border-r border-border bg-muted/30 p-2">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </nav>

            {/* Info Box */}
            <div className="mt-6 p-3 bg-muted rounded-lg">
              <h4 className="text-sm font-medium mb-1">Enterprise SSO</h4>
              <p className="text-xs text-muted-foreground">
                Configure single sign-on and automated user provisioning for your organization.
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'organization' && (
              <OrganizationSettingsWrapper onOrgSelect={setSelectedOrgId} />
            )}
            {activeTab === 'idp' && (
              selectedOrgId ? (
                <IdPConfigForm orgId={selectedOrgId} />
              ) : (
                <NoOrgSelected
                  message="Select or create an organization first to configure identity providers."
                  onGoToOrg={() => setActiveTab('organization')}
                />
              )
            )}
            {activeTab === 'scim' && (
              selectedOrgId ? (
                <SCIMSettings orgId={selectedOrgId} />
              ) : (
                <NoOrgSelected
                  message="Select or create an organization first to configure SCIM provisioning."
                  onGoToOrg={() => setActiveTab('organization')}
                />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Wrapper to pass org selection up
function OrganizationSettingsWrapper({ onOrgSelect }: { onOrgSelect: (orgId: string | null) => void }) {
  return (
    <div>
      <OrganizationSettingsWithCallback onOrgSelect={onOrgSelect} />
    </div>
  );
}

// Modified OrganizationSettings that reports selected org
import { useAuth } from '../../lib/auth/AuthProvider';
import { useCallback, useEffect, useState as useStateHook } from 'react';

interface Organization {
  id: string;
  name: string;
  slug: string;
  domain: string;
  domainVerified: boolean;
  ssoEnabled: boolean;
  ssoEnforced: boolean;
  role: 'owner' | 'admin' | 'member';
  createdAt: string;
}

function OrganizationSettingsWithCallback({ onOrgSelect }: { onOrgSelect: (orgId: string | null) => void }) {
  const { config, getAccessToken } = useAuth();
  const [organizations, setOrganizations] = useStateHook<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useStateHook<Organization | null>(null);
  const [isLoading, setIsLoading] = useStateHook(true);
  const [error, setError] = useStateHook<string | null>(null);
  const [success, setSuccess] = useStateHook<string | null>(null);

  // Create org form
  const [showCreateForm, setShowCreateForm] = useStateHook(false);
  const [newOrgName, setNewOrgName] = useStateHook('');
  const [newOrgSlug, setNewOrgSlug] = useStateHook('');
  const [newOrgDomain, setNewOrgDomain] = useStateHook('');
  const [isCreating, setIsCreating] = useStateHook(false);

  // Domain verification
  interface DomainVerificationInfo {
    method: 'dns' | 'file';
    recordType?: string;
    recordName?: string;
    recordValue?: string;
    filePath?: string;
    fileContent?: string;
    instructions: string;
  }
  const [verificationInfo, setVerificationInfo] = useStateHook<DomainVerificationInfo | null>(null);
  const [isVerifying, setIsVerifying] = useStateHook(false);

  // API helper
  const apiCall = useCallback(async (path: string, options?: RequestInit) => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${config?.api?.endpoint}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options?.headers,
      },
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || 'API request failed');
    }
    return data.data;
  }, [config, getAccessToken]);

  // Load organizations
  const loadOrganizations = useCallback(async () => {
    try {
      setIsLoading(true);
      const orgs = await apiCall('/organizations');
      setOrganizations(orgs);
      if (orgs.length > 0 && !selectedOrg) {
        setSelectedOrg(orgs[0]);
        onOrgSelect(orgs[0].id);
      }
    } catch (err: any) {
      // Organizations endpoint might not have data yet
      setOrganizations([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiCall, selectedOrg, onOrgSelect]);

  useEffect(() => {
    if (config?.api?.endpoint) {
      loadOrganizations();
    }
  }, [config]);

  // Update parent when org changes
  useEffect(() => {
    onOrgSelect(selectedOrg?.id || null);
  }, [selectedOrg, onOrgSelect]);

  // Create organization
  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsCreating(true);

    try {
      const newOrg = await apiCall('/organizations', {
        method: 'POST',
        body: JSON.stringify({
          name: newOrgName,
          slug: newOrgSlug || undefined,
          domain: newOrgDomain,
        }),
      });

      setOrganizations([...organizations, newOrg]);
      setSelectedOrg(newOrg);
      onOrgSelect(newOrg.id);
      setShowCreateForm(false);
      setNewOrgName('');
      setNewOrgSlug('');
      setNewOrgDomain('');
      setSuccess('Organization created successfully');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  // Update organization
  const handleUpdateOrg = async (updates: Partial<Organization>) => {
    if (!selectedOrg) return;
    setError(null);

    try {
      const updated = await apiCall(`/organizations/${selectedOrg.id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });

      setOrganizations(organizations.map(o =>
        o.id === selectedOrg.id ? { ...o, ...updated } : o
      ));
      setSelectedOrg({ ...selectedOrg, ...updated });
      setSuccess('Organization updated successfully');
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Initiate domain verification
  const handleInitiateVerification = async (method: 'dns' | 'file') => {
    if (!selectedOrg) return;
    setError(null);
    setIsVerifying(true);

    try {
      const info = await apiCall(`/organizations/${selectedOrg.id}/verify-domain`, {
        method: 'POST',
        body: JSON.stringify({ method }),
      });

      setVerificationInfo(info);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsVerifying(false);
    }
  };

  // Confirm domain verification
  const handleConfirmVerification = async () => {
    if (!selectedOrg || !verificationInfo) return;
    setError(null);
    setIsVerifying(true);

    try {
      await apiCall(`/organizations/${selectedOrg.id}/confirm-domain`, {
        method: 'POST',
        body: JSON.stringify({
          token: verificationInfo.recordValue || verificationInfo.fileContent,
        }),
      });

      setSelectedOrg({ ...selectedOrg, domainVerified: true });
      setOrganizations(organizations.map(o =>
        o.id === selectedOrg.id ? { ...o, domainVerified: true } : o
      ));
      setVerificationInfo(null);
      setSuccess('Domain verified successfully');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsVerifying(false);
    }
  };

  const isAdmin = selectedOrg?.role === 'owner' || selectedOrg?.role === 'admin';

  if (isLoading) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        Loading organizations...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">Organization Settings</h2>
        <p className="text-muted-foreground">
          Manage your organization, domain verification, and SSO settings.
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-md">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {/* Organization Selector */}
      <div className="mb-6 flex items-center gap-4">
        <select
          value={selectedOrg?.id || ''}
          onChange={(e) => {
            const org = organizations.find(o => o.id === e.target.value);
            setSelectedOrg(org || null);
            setVerificationInfo(null);
          }}
          className="flex-1 px-3 py-2 bg-background border border-input rounded-md"
        >
          {organizations.length === 0 ? (
            <option value="">No organizations</option>
          ) : (
            organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name} ({org.role})
              </option>
            ))
          )}
        </select>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Create Organization
        </button>
      </div>

      {/* Create Organization Form */}
      {showCreateForm && (
        <div className="mb-6 p-4 bg-muted rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Create Organization</h3>
          <form onSubmit={handleCreateOrg} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="Acme Inc"
                className="w-full px-3 py-2 bg-background border border-input rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Slug (optional)</label>
              <input
                type="text"
                value={newOrgSlug}
                onChange={(e) => setNewOrgSlug(e.target.value)}
                placeholder="acme-inc"
                className="w-full px-3 py-2 bg-background border border-input rounded-md"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Used in SSO login URLs. Auto-generated from name if not provided.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Domain</label>
              <input
                type="text"
                value={newOrgDomain}
                onChange={(e) => setNewOrgDomain(e.target.value)}
                placeholder="acme.com"
                className="w-full px-3 py-2 bg-background border border-input rounded-md"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Email domain for SSO discovery (e.g., employees@acme.com)
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isCreating}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Selected Organization Details */}
      {selectedOrg && (
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="p-4 bg-muted rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Organization Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-muted-foreground">Name</label>
                <p className="font-medium">{selectedOrg.name}</p>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground">Slug</label>
                <p className="font-medium">{selectedOrg.slug}</p>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground">Domain</label>
                <p className="font-medium">{selectedOrg.domain}</p>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground">Your Role</label>
                <p className="font-medium capitalize">{selectedOrg.role}</p>
              </div>
            </div>
          </div>

          {/* Domain Verification */}
          <div className="p-4 bg-muted rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Domain Verification</h3>

            {selectedOrg.domainVerified ? (
              <div className="flex items-center gap-2 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Domain verified</span>
              </div>
            ) : isAdmin ? (
              <div className="space-y-4">
                {!verificationInfo ? (
                  <>
                    <p className="text-muted-foreground">
                      Verify domain ownership to enable SSO for your organization.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleInitiateVerification('dns')}
                        disabled={isVerifying}
                        className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 disabled:opacity-50"
                      >
                        Verify via DNS
                      </button>
                      <button
                        onClick={() => handleInitiateVerification('file')}
                        disabled={isVerifying}
                        className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 disabled:opacity-50"
                      >
                        Verify via File
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="p-3 bg-background rounded border">
                      <p className="font-medium mb-2">Verification Instructions:</p>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {verificationInfo.instructions}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleConfirmVerification}
                        disabled={isVerifying}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                      >
                        {isVerifying ? 'Verifying...' : 'Confirm Verification'}
                      </button>
                      <button
                        onClick={() => setVerificationInfo(null)}
                        className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">
                Domain verification pending. Contact an admin to verify.
              </p>
            )}
          </div>

          {/* SSO Settings */}
          {isAdmin && (
            <div className="p-4 bg-muted rounded-lg">
              <h3 className="text-lg font-semibold mb-4">SSO Settings</h3>

              {!selectedOrg.domainVerified ? (
                <p className="text-muted-foreground">
                  Verify your domain to enable SSO settings.
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Enable SSO</p>
                      <p className="text-sm text-muted-foreground">
                        Allow users to sign in with your identity provider
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedOrg.ssoEnabled}
                        onChange={(e) => handleUpdateOrg({ ssoEnabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {selectedOrg.ssoEnabled && (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Enforce SSO</p>
                        <p className="text-sm text-muted-foreground">
                          Require all users with your domain to use SSO
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedOrg.ssoEnforced}
                          onChange={(e) => handleUpdateOrg({ ssoEnforced: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper component for when no org is selected
function NoOrgSelected({ message, onGoToOrg }: { message: string; onGoToOrg: () => void }) {
  return (
    <div className="p-6 flex flex-col items-center justify-center h-full text-center">
      <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
      <p className="text-muted-foreground mb-4">{message}</p>
      <button
        onClick={onGoToOrg}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
      >
        Go to Organization Settings
      </button>
    </div>
  );
}
